import React, { useState, useEffect, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Modal } from "@mantine/core";
import "./explore-tests.css";
import LazyBackground from "../../Lazy/lazy-background";
import LoadingDashboard from "../../loading/LoadingDashboard";
import { GlobalContext } from "../../../GlobalContext";
import { useNavigate } from "react-router";

// Format price (same as Tiles.jsx)
const formatPrice = (amount) => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return "₦0.00";
  }
  let numAmount;
  if (typeof amount === "string") {
    const cleanAmount = amount.replace(/[₦,]/g, "").replace(/\.00$/, "");
    numAmount = parseFloat(cleanAmount);
  } else {
    numAmount = parseFloat(amount);
  }
  if (isNaN(numAmount)) {
    return "₦0.00";
  }
  return `₦${numAmount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const ExploreTests = () => {
  const navigate = useNavigate();
  const { userData, isLoading: globalIsLoading } = useContext(GlobalContext);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [direction, setDirection] = useState(0);
  const [opened, setOpened] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);
  const [tests, setTests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_ENDPOINT = "/wp-json/dbsb/v1/medical-tests";
  const testsPerPage = 3;

  // Fetch tests from API
  useEffect(() => {
    const fetchTests = async () => {
      setIsLoading(true);
      setError(null);
      try {
        if (!window.wpApiSettings?.nonce) {
          throw new Error(
            "Authentication token not found. Please try logging in again."
          );
        }

        const response = await fetch(`${API_ENDPOINT}?per_page=100`, {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "X-WP-Nonce": window.wpApiSettings.nonce,
          },
        });

        if (!response.ok) {
          if (response.status === 401) {
            throw new Error(
              "You must be logged in to view tests. Please log in and try again."
            );
          } else if (response.status === 403) {
            throw new Error("You don't have permission to view tests.");
          } else {
            throw new Error("Failed to fetch tests. Please try again.");
          }
        }

        const data = await response.json();

        const mappedTests = data.tests.map((test) => ({
          id: test.id.toString(),
          slug: test.slug,
          name: test.title || "N/A",
          info: test.excerpt || "N/A",
          price: test.test_price ? formatPrice(test.test_price) : "N/A",
          processingTime: test.processing_time || "N/A",
          sampleType: test.sample_type || "N/A",
          content: test.content || "N/A",
        }));

        setTests(mappedTests);
      } catch (err) {
        console.error("Error fetching tests:", err);
        setError(err.message);
        setTests([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTests();
  }, []);

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const totalTests = tests.length;
  const canGoNext = isMobile
    ? currentIndex < totalTests - 1
    : currentIndex < totalTests - testsPerPage;
  const canGoPrevious = currentIndex > 0;

  const getCurrentTests = () => {
    const startIndex = currentIndex;
    const endIndex = Math.min(startIndex + testsPerPage, totalTests);
    return tests.slice(startIndex, endIndex);
  };

  const handleNext = () => {
    if (canGoNext) {
      setDirection(1);
      setCurrentIndex((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (canGoPrevious) {
      setDirection(-1);
      setCurrentIndex((prev) => prev - 1);
    }
  };

  const getAnimatingBoxIndex = () => {
    if (direction === 1) {
      return isMobile ? 0 : testsPerPage - 1;
    } else if (direction === -1) {
      return 0;
    }
    return -1;
  };

  const handleLearnMore = (test) => {
    setSelectedTest(test);
    setOpened(true);
  };

const handleOrderTest = (test) => {
  if (userData.email === "N/A" || globalIsLoading) {
    navigate("/login");
  } else {
    navigate(`/order-test?test_slug=${test.slug}`);
  }
};

  // Animations
  const tileVariants = {
    enter: {
      opacity: 0,
      scale: 0.9,
      filter: "blur(6px)",
      rotateY: direction === 1 ? 15 : -15,
    },
    center: {
      opacity: 1,
      scale: 1,
      filter: "blur(0px)",
      rotateY: 0,
    },
    exit: {
      opacity: 0,
      scale: 0.9,
      filter: "blur(6px)",
      rotateY: direction === 1 ? -15 : 15,
    },
  };

  const sectionVariants = {
    hidden: { opacity: 1 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: "spring", stiffness: 100 },
    },
  };

  return (
    <motion.section
      id="our-tests"
      className="explore-tests-container"
      variants={sectionVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
    >
      {/* Background decorations */}
      <LazyBackground
        src={`${process.env.PUBLIC_URL}/assets/background4.svg`}
        alt=""
        className="home-background home-background4"
        loading="lazy"
      />
      <LazyBackground
        src={`${process.env.PUBLIC_URL}/assets/background6.svg`}
        alt=""
        className="home-background home-background6"
        loading="lazy"
      />

      {/* Header */}
      <motion.div className="explore-tests-header" variants={itemVariants}>
        <h2>Explore Our Genomic Tests</h2>
        <p>
          Choose from a wide range of genetic tests designed to provide you with
          critical insights into your health. Order with just a few steps by
          clicking below.
        </p>
      </motion.div>

      {/* Tiles section */}
      <motion.div variants={itemVariants}>
        {isLoading ? (
          <LoadingDashboard />
        ) : error ? (
          <motion.div
            className="error"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            Error: {error}
          </motion.div>
        ) : tests.length > 0 ? (
          <>
            <div className="tiles-wrapper">
              <div className="tiles-grid">
                {getCurrentTests().map((test, index) => {
                  const shouldAnimate = index === getAnimatingBoxIndex();
                  const key = `${test.id}-${currentIndex}-${index}`;
                  return shouldAnimate ? (
                    <AnimatePresence key={key} mode="wait">
                      <motion.div
                        variants={tileVariants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        className="test-tile"
                        whileHover={{ scale: 1, y: 0 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <h3>{test.name}</h3>
                        <p className="test-info">{test.info}</p>
                        <p className="test-price">{test.price}</p>
                        <p className="processing-label">
                          Estimated processing time
                        </p>
                        <p className="processing-time">{test.processingTime}</p>
                        <button
                          className="learn-more-btn"
                          onClick={() => handleLearnMore(test)}
                        >
                          Learn more
                        </button>
                      </motion.div>
                    </AnimatePresence>
                  ) : (
                    <motion.div
                      key={key}
                      className="test-tile"
                      whileHover={{ scale: 1, y: 0 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <h3>{test.name}</h3>
                      <p className="test-info">{test.info}</p>
                      <p className="test-price">{test.price}</p>
                      <p className="processing-label">
                        Estimated processing time
                      </p>
                      <p className="processing-time">{test.processingTime}</p>
                      <button
                        className="learn-more-btn"
                        onClick={() => handleLearnMore(test)}
                      >
                        Learn more
                      </button>
                    </motion.div>
                  );
                })}
              </div>
            </div>

            {/* Arrows */}
            <div className="arrows-container">
              <motion.button
                className={`arrow-btn ${!canGoPrevious ? "disabled" : ""}`}
                onClick={handlePrevious}
                disabled={!canGoPrevious}
              >
                <ChevronLeft size={24} />
              </motion.button>
              <motion.button
                className={`arrow-btn ${!canGoNext ? "disabled" : ""}`}
                onClick={handleNext}
                disabled={!canGoNext}
              >
                <ChevronRight size={24} />
              </motion.button>
            </div>
          </>
        ) : (
          <motion.div
            className="no-tests"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <p>No tests available</p>
          </motion.div>
        )}
      </motion.div>

      {/* Modal */}
      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        size="xl"
        padding="2rem"
        radius="12px"
        centered
        className="modal-test-details-modal"
      >
        {selectedTest && (
          <AnimatePresence>
            <motion.div
              className="modal-test-detail-modal"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
            >
              <motion.div
                className="modal-test-detail-left"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1, duration: 0.3 }}
              >
                <h2>Test details</h2>
                <div className="modal-test-details-box">
                  <h3>{selectedTest.name}</h3>
                  <div className="modal-detail-item">
                    <span className="modal-detail-label">Price</span>
                    <span className="modal-detail-value modal-price-detail-value">
                      {selectedTest.price}
                    </span>
                  </div>
                  <div className="modal-detail-item">
                    <span className="modal-detail-label">
                      Estimated processing time
                    </span>
                    <span className="modal-detail-value">
                      {selectedTest.processingTime}
                    </span>
                  </div>
                  <div className="modal-detail-item">
                    <span className="modal-detail-label">
                      Sample type required
                    </span>
                    <span className="modal-detail-value">
                      {selectedTest.sampleType}
                    </span>
                  </div>
                </div>
                <div className="modal-divider"></div>
                <motion.button
                  className="modal-order-button modal-tiles-order-button"
                  onClick={() => handleOrderTest(selectedTest)}
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Order this test
                </motion.button>
                <motion.button
                  className="modal-go-back-button"
                  onClick={() => setOpened(false)}
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Go back
                </motion.button>
              </motion.div>

              <motion.div
                className="modal-test-detail-right"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.15, duration: 0.3 }}
              >
                <div className="modal-subheader">
                  What is {selectedTest.name}?
                </div>
                <div
                  className="modal-test-excerpt"
                  dangerouslySetInnerHTML={{
                    __html: (selectedTest.content || "")
                      .replace(/\n/g, "")
                      .replace(/<p>/g, '<p style="margin-bottom: 0.8rem;">')
                  }}
                />
              </motion.div>
            </motion.div>
          </AnimatePresence>
        )}
      </Modal>
    </motion.section>
  );
};

export default ExploreTests;
